import { motion } from 'framer-motion'
import { Presentation } from 'lucide-react'
import { useState, useEffect } from 'react'
import { generateThumbnail, generateFallbackThumbnail } from '@/lib/presentation/thumbnails'
import type { SlideData } from '@/types/presentation'
import { useAuth } from '@/hooks/useAuth'
import { AccessLevelBadge } from './AccessLevelBadge'

export interface PresentationItem {
  id: string
  title: string
  description: string
  type: string
  accessLevel: string
  companyId: string | null
  filePath: string
  thumbnailUrl: string | null
  slides?: SlideData[] // Optional slide data for thumbnail generation
  metadata: {
    slideCount?: number
    author?: string
    duration?: number
    tags?: string[]
    viewCount?: number
  } | null
  createdAt: string
  updatedAt: string
}

interface PresentationCardProps {
  presentation: PresentationItem
  viewMode?: 'grid' | 'list'
  onClick?: (presentation: PresentationItem) => void
}

export function PresentationCard({ presentation, viewMode = 'grid', onClick }: PresentationCardProps) {
  // Get user auth state for badge visibility
  const { isAdmin } = useAuth()

  // Thumbnail state
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(presentation.thumbnailUrl)
  const [isGenerating, setIsGenerating] = useState(false)

  // Generate thumbnail on mount if we have slide data but no thumbnail
  useEffect(() => {
    const generateThumbnailIfNeeded = async () => {
      // Skip if we already have a thumbnail
      if (thumbnailUrl) {
        return
      }

      // If we have slide data, try to generate a thumbnail
      if (presentation.slides && presentation.slides.length > 0) {
        setIsGenerating(true)
        try {
          // Generate thumbnail from first slide, passing presentation title as fallback
          const firstSlide = presentation.slides[0]
          const generatedThumbnail = await generateThumbnail(
            presentation.id,
            firstSlide,
            0,
            presentation.title
          )
          setThumbnailUrl(generatedThumbnail)
        } catch (error) {
          console.error('Failed to generate thumbnail:', error)
          // Use fallback thumbnail with slide count
          const fallback = generateFallbackThumbnail(
            presentation.title,
            presentation.metadata?.slideCount
          )
          setThumbnailUrl(fallback)
        } finally {
          setIsGenerating(false)
        }
      } else {
        // No slide data - generate fallback immediately with slide count from metadata
        const fallback = generateFallbackThumbnail(
          presentation.title,
          presentation.metadata?.slideCount
        )
        setThumbnailUrl(fallback)
      }
    }

    generateThumbnailIfNeeded()
  }, [presentation.id, presentation.slides, presentation.title, presentation.metadata?.slideCount, thumbnailUrl])

  const cardContent = (
    <>
      {/* Thumbnail or Icon */}
      <div className={`relative ${viewMode === 'grid' ? 'aspect-video' : 'w-48 h-32 flex-shrink-0'} bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg overflow-hidden group-hover:from-blue-500/30 group-hover:to-purple-500/30 transition-colors`}>
        {isGenerating ? (
          // Loading state with pulse animation
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500/10 to-purple-500/10 animate-pulse">
            <Presentation className="w-12 h-12 text-blue-500/50" strokeWidth={1.5} />
          </div>
        ) : thumbnailUrl ? (
          // Display thumbnail with subtle zoom on hover
          <img
            src={thumbnailUrl}
            alt={presentation.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          // Enhanced fallback with gradient and icon
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600">
            <Presentation className="w-12 h-12 text-white/90 mb-2" strokeWidth={1.5} />
            <span className="text-white/80 text-sm font-medium">No preview</span>
          </div>
        )}

        {/* Access Level Badge - only visible to admins */}
        {isAdmin && (
          <AccessLevelBadge
            accessLevel={presentation.accessLevel as 'demo' | 'company' | 'admin'}
            position="top-right"
          />
        )}
      </div>

      {/* Content */}
      <div className={`${viewMode === 'grid' ? '' : 'flex-1 ml-4'} space-y-2`}>
        <div>
          <h3 className="font-semibold text-lg line-clamp-1">{presentation.title}</h3>
          <p className={`text-sm text-muted-foreground ${viewMode === 'grid' ? 'line-clamp-2' : 'line-clamp-1'}`}>
            {presentation.description}
          </p>
        </div>

        {/* Metadata */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          {presentation.metadata?.slideCount && (
            <span>{presentation.metadata.slideCount} slides</span>
          )}
          {presentation.metadata?.duration && (
            <span>{Math.round(presentation.metadata.duration / 60)} min</span>
          )}
          <span>
            {new Date(presentation.createdAt).toLocaleDateString()}
          </span>
        </div>

        {/* Tags */}
        {presentation.metadata?.tags && presentation.metadata.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {presentation.metadata.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="text-xs px-2 py-0.5 bg-secondary text-secondary-foreground rounded-full"
              >
                {tag}
              </span>
            ))}
            {presentation.metadata.tags.length > 3 && (
              <span className="text-xs px-2 py-0.5 bg-secondary text-secondary-foreground rounded-full">
                +{presentation.metadata.tags.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
    </>
  )

  return (
    <motion.div
      className={`
        ${viewMode === 'grid'
          ? 'space-y-3'
          : 'flex items-start p-4 bg-card rounded-lg border hover:border-primary/50'
        }
        cursor-pointer group
      `}
      onClick={() => onClick?.(presentation)}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {cardContent}
    </motion.div>
  )
}
